import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { useAppSelector } from "flavours/glitch/store";

// Page background per palette and mode, used for the browser's theme colour.
const PALETTE_BACKGROUNDS = {
  moss: { dark: "#111612", light: "#f0f4f0" },
  slate: { dark: "#121316", light: "#f2f3f5" },
  sloth: { dark: "#16130f", light: "#f7f2ec" },
  classic: { dark: "#191b22", light: "#eff3f5" },
  indigo: { dark: "#0e0a1f", light: "#efecf8" },
};

const CONTRAST_BACKGROUND = PALETTE_BACKGROUNDS.moss.dark;

/**
 * Picks the stylesheet for the theme (light, dark, contrast) and the colour palette.
 * Moss is built into the main stylesheet (dark) and the mastodon-light skin (light),
 * the other palettes are skins named "<palette>-<mode>".
 * @param {string} theme
 * @param {string} palette
 * @returns {string} stylesheet URL, or "" for the main stylesheet alone
 */
const stylesheetFor = (theme, palette) => {
  if (theme === "contrast") {
    return "/packs/css/skins/glitch/contrast/common.css";
  }

  const mode = theme === "mastodon" ? "dark" : "light";

  if (palette === "moss") {
    return mode === "dark" ? "" : "/packs/css/skins/glitch/mastodon-light/common.css";
  }

  return `/packs/css/skins/glitch/${palette}-${mode}/common.css`;
};

const darkQuery = () => window.matchMedia?.("(prefers-color-scheme: dark)");

// Follows the light or dark mode of the device while the page is open.
const useSystemDark = () => {
  const [dark, setDark] = useState(() => !!darkQuery()?.matches);

  useEffect(() => {
    const query = darkQuery();
    if (!query) {
      return undefined;
    }
    const update = () => setDark(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return dark;
};

const ThemeComponent = ({ children }) => {
  const storedTheme = useAppSelector(
    (state) => state.getIn(["local_settings", "theme"]) ?? "mastodon-light",
  );
  const systemDark = useSystemDark();
  const theme = storedTheme === "auto" ? (systemDark ? "mastodon" : "mastodon-light") : storedTheme;
  const storedPalette = useAppSelector(
    (state) => state.getIn(["local_settings", "palette"]) ?? "moss",
  );
  const palette = storedPalette in PALETTE_BACKGROUNDS ? storedPalette : "moss";

  const href = stylesheetFor(theme, palette);

  useEffect(() => {
    const meta = document.querySelector("meta[name=theme-color]");
    if (!meta) {
      return;
    }

    const color = theme === "contrast"
      ? CONTRAST_BACKGROUND
      : PALETTE_BACKGROUNDS[palette][theme === "mastodon" ? "dark" : "light"];
    meta.setAttribute("content", color);
  }, [theme, palette]);

  return (
    <>
      {href !== "" ? <link rel='stylesheet' media='all' href={href} /> : null}
      {children}
    </>
  );
};

ThemeComponent.propTypes = {
  children: PropTypes.node,
};

export { ThemeComponent };
